import { Loader } from '@progress/kendo-react-indicators';
import clsx from 'clsx';
import { get } from 'lodash';
import { type FC, type MouseEvent, type ReactElement } from 'react';
import { useImage } from 'react-image';
import { handlePostMessage } from 'src/hooks/usePostMessage';
import { DEFAULT_PLACEHOLDERS } from 'src/utils/parseProductImageUrl';

interface ProductImageProps {
  productId: number;
  productKey: string;
  supplierId: number;
  linkUrl: string;
  altText: string;
  containerHeight?: string;
  containerWidth?: string;
  imageHeight: string;
  imageWidth: string;
  imageClassName?: string;
  containerClassName?: string;
  type: 'summary' | 'detail';
  // Notify the parent application when the image fails to load (Catalog Products only).
  reportBrokenImage?: boolean;
}

// Preload image using native Image API (behaves like <img> tag, no CORS issues)
const preloadImage = (url: string): Promise<void> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve();
    img.onerror = (error) => reject(error);
    img.src = url;
  });

export const ProductImage: FC<ProductImageProps> = ({
  productId,
  productKey,
  supplierId,
  linkUrl,
  altText,
  containerHeight = '100%',
  containerWidth = '100%',
  imageHeight,
  imageWidth,
  imageClassName,
  containerClassName,
  type = 'detail',
  reportBrokenImage = false,
}: ProductImageProps): ReactElement<HTMLImageElement> => {
  const {
    src,
    isLoading: isImageLoading,
    error: imageError,
  } = useImage({
    useSuspense: false,
    srcList: [linkUrl],
    imgPromise: async (...args: any[]): Promise<void> => {
      const rawSrc: string = args[0];
      if (rawSrc === DEFAULT_PLACEHOLDERS.noImage) {
        return;
      }
      try {
        await preloadImage(rawSrc);
      } catch (err) {
        if (reportBrokenImage) {
          // Log error for tracking but don't block image loading
          const error = err as Error;
          const payload = {
            url: rawSrc,
            supplierId,
            productId,
            error: get(error, 'message', get(err, 'stack', '')),
          };
          handlePostMessage({
            brokenImage: payload,
          });
        }

        throw err;
      }
    },
  });

  if (type === 'summary') {
    return (
      <>
        {isImageLoading ? (
          <div
            style={{
              height: imageHeight,
              width: imageWidth,
              display: 'grid',
              placeItems: 'center',
              backgroundColor: 'gray-200',
            }}
          >
            <Loader type="converging-spinner" size="small" />
          </div>
        ) : imageError ? (
          <img
            id={productKey}
            src={DEFAULT_PLACEHOLDERS.brokenImage}
            alt={altText}
            onContextMenu={(e: MouseEvent<HTMLImageElement>) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            className={clsx(
              `h-[${imageHeight}] w-[${imageWidth}] object-contain bg-gray-200`,
              'cursor-not-allowed',
              imageClassName
            )}
          />
        ) : (
          <img
            id={productKey}
            src={src}
            alt={altText}
            {...([
              DEFAULT_PLACEHOLDERS.brokenImage,
              DEFAULT_PLACEHOLDERS.noImage,
              DEFAULT_PLACEHOLDERS.websiteIcon,
            ].includes(src as string)
              ? {
                  onContextMenu: (e: MouseEvent<HTMLImageElement>) => {
                    e.preventDefault();
                    e.stopPropagation();
                  },
                }
              : {})}
            className={clsx(
              `h-[${imageHeight}] w-[${imageWidth}] bg-gray-200`,
              [
                DEFAULT_PLACEHOLDERS.brokenImage,
                DEFAULT_PLACEHOLDERS.noImage,
                DEFAULT_PLACEHOLDERS.websiteIcon,
              ].includes(src as string)
                ? 'object-contain'
                : 'object-cover',
              imageClassName
            )}
          />
        )}
      </>
    );
  }

  return (
    <div
      style={{
        width: containerWidth,
        height: containerHeight,
        display: 'grid',
        placeItems: 'center',
      }}
      className={containerClassName}
    >
      {isImageLoading ? (
        <Loader type="converging-spinner" size="small" />
      ) : imageError ? (
        <img
          id={productKey}
          src={DEFAULT_PLACEHOLDERS.brokenImage}
          alt={altText}
          onContextMenu={(e: MouseEvent<HTMLImageElement>) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          className={clsx(
            `h-[${imageHeight}] w-[${imageWidth}] object-contain bg-gray-200`,
            'cursor-not-allowed',
            imageClassName
          )}
        />
      ) : (
        <img
          id={productKey}
          src={src}
          alt={altText}
          {...([
            DEFAULT_PLACEHOLDERS.brokenImage,
            DEFAULT_PLACEHOLDERS.noImage,
            DEFAULT_PLACEHOLDERS.websiteIcon,
          ].includes(src as string)
            ? {
                onContextMenu: (e: MouseEvent<HTMLImageElement>) => {
                  e.preventDefault();
                  e.stopPropagation();
                },
              }
            : {})}
          className={clsx(
            `h-[${imageHeight}] w-[${imageWidth}] bg-gray-200`,
            [
              DEFAULT_PLACEHOLDERS.brokenImage,
              DEFAULT_PLACEHOLDERS.noImage,
              DEFAULT_PLACEHOLDERS.websiteIcon,
            ].includes(src as string)
              ? 'object-contain'
              : 'object-cover',
            imageClassName
          )}
        />
      )}
    </div>
  );
};
